import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { teacherApi } from '../../services/api';
import StoriesBar from '../../components/StoriesBar';

const F = { fontFamily: 'Inter' } as const;

// Real 2026 official exam dates (YÖK/ÖSYM / MEB)
const YKS_DATE = new Date('2026-06-14T00:00:00');   // First session
const LGS_DATE = new Date('2026-06-07T00:00:00');

function daysUntilDate(target: Date): number {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / 86400000);
}

function daysUntil(dateStr: string | null): number {
  if (!dateStr) return 0;
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
}

const TeacherDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState<any>(null);
  const [books, setBooks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      teacherApi.dashboard(),
      teacherApi.library(),
    ])
      .then(([dashRes, libRes]) => {
        setDashboard(dashRes.data);
        setBooks((libRes.data || []).slice(0, 5));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const classes: any[] = dashboard?.classes || [];
  const assignments: any[] = dashboard?.assignments || [];
  const appointments: any[] = dashboard?.appointments || [];

  const yksGun = daysUntilDate(YKS_DATE);
  const lgsGun = daysUntilDate(LGS_DATE);

  const upcomingHomework = assignments.slice(0, 6).map((a: any, i: number) => ({
    id: String(a.id),
    subject: `${a.subject?.name || ''}${a.title ? ' - ' + a.title : ''}`,
    detail: a.due_date ? new Date(a.due_date).toLocaleDateString('tr-TR') : 'Süresiz',
    days: a.due_date ? daysUntil(a.due_date) : 0,
    color: i % 2 === 0 ? '#3973bf' : '#e5599b',
  }));

  const displayClasses = classes.slice(0, 6).map((c: any, i: number) => ({
    id: String(c.id),
    name: c.campuses?.institutions?.name || 'Kurum',
    code: c.name,
    students: c.class_student_enrollments?.length || 0,
    cover: `/a-sinif-tiling${(i % 4) + 1}.png`,
  }));

  const nextAppt = appointments[0];

  return (
    <div>
      {/* Side-by-side only from xl — below that the fixed-width Yaklaşan
          Ödevler column starves the left column past its min-content width and
          the two overlap. */}
      <div className="flex flex-col xl:flex-row gap-4 mb-4 xl:items-stretch">
        {/* ── LEFT COLUMN (Stories → YKS/LGS → Hoşgeldin/Randevular) ── */}
        <div className="flex-1 min-w-0 flex flex-col gap-3">
          <StoriesBar />
          {/* Sayaç + Hoşgeldin (left) | Randevularım (right, spans full height) */}
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            {/* Left sub-column: compact YKS/LGS counters on top, greeting below */}
            <div className="flex-1 min-w-0 flex flex-col gap-3">
              {/* YKS + LGS countdown row — teachers get a compact counter (B18) */}
              <div className="flex gap-3">
                <div className="flex-1 rounded-[16px] px-3 py-2 text-center" style={{ background: '#3973bf', boxShadow: '0px 1px 4px rgba(0,0,0,0.25)' }}>
                  <p style={{ ...F, fontSize: 11, fontWeight: 500, color: '#fafafa' }}>YKS SAYAÇ</p>
                  <p style={{ ...F, fontSize: 22, fontWeight: 600, color: '#fafafa' }}>
                    {yksGun > 0 ? yksGun : 'Geçti'}
                  </p>
                  <p style={{ ...F, fontSize: 11, fontWeight: 500, color: '#fafafa' }}>
                    {yksGun > 0 ? 'GÜN' : '2026'}
                  </p>
                </div>
                <div className="flex-1 rounded-[16px] px-3 py-2 text-center" style={{ background: '#e5599b', boxShadow: '0px 1px 4px rgba(0,0,0,0.25)' }}>
                  <p style={{ ...F, fontSize: 11, fontWeight: 500, color: '#fafafa' }}>LGS SAYAÇ</p>
                  <p style={{ ...F, fontSize: 22, fontWeight: 600, color: '#fafafa' }}>
                    {lgsGun > 0 ? lgsGun : 'Geçti'}
                  </p>
                  <p style={{ ...F, fontSize: 11, fontWeight: 500, color: '#fafafa' }}>
                    {lgsGun > 0 ? 'GÜN' : '2026'}
                  </p>
                </div>
              </div>

              {/* Hoşgeldin card */}
              <div className="bg-[#fafafa] rounded-[20px] px-5 py-4 flex items-center gap-4 flex-1" style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
                <div className="flex-1">
                  <p style={{ ...F, fontSize: 18, fontWeight: 300, color: '#2b2b2b' }}>Hoş geldin Öğretmen,</p>
                  <p style={{ ...F, fontSize: 26, fontWeight: 500, color: '#2b2b2b' }}>{user?.name}</p>
                </div>
                <img src={user?.avatarUrl || '/a-avatar.png'} alt="" className="w-[80px] h-[80px] xl:w-[100px] xl:h-[100px] rounded-full object-cover flex-shrink-0" />
              </div>
            </div>

            {/* Randevularım — spans the full sub-column height (top aligns with the sayaç row) */}
            <div
              onClick={() => navigate('/teacher/randevularim')}
              className="w-full sm:w-[200px] flex-shrink-0 bg-[#fafafa] rounded-[20px] overflow-hidden flex flex-col cursor-pointer hover:shadow-md transition-shadow"
              style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
            >
              <div className="bg-[#3973bf] px-4 py-2.5 text-center">
                <span style={{ ...F, fontSize: 14, fontWeight: 500, color: '#fafafa' }}>Randevularım</span>
              </div>
              <div className="flex-1 flex flex-col items-center justify-center px-3 py-3 text-center">
                {nextAppt ? (
                  <>
                    <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#3973bf' }}>
                      {nextAppt.subjects?.name || 'Randevu'}
                    </p>
                    <p style={{ ...F, fontSize: 13, fontWeight: 300, color: '#3973bf' }}>
                      {nextAppt.users_appointments_student_user_idTousers?.first_name || ''}
                    </p>
                    <p style={{ ...F, fontSize: 13, fontWeight: 300, color: '#3973bf' }}>
                      {nextAppt.appointmentDate ? new Date(nextAppt.appointmentDate).toLocaleDateString('tr-TR') : '-'}
                    </p>
                  </>
                ) : (
                  <p style={{ ...F, fontSize: 13, color: '#3973bf' }}>Randevu yok</p>
                )}
              </div>
              {nextAppt?.appointmentDate && daysUntil(nextAppt.appointmentDate) >= 0 && (
                <div className="px-3 py-2 text-center">
                  {/* Past dates must not render as "Bugün" — see student/parent
                      dashboards, same fix. */}
                  <span style={{ ...F, fontSize: 13, fontWeight: 500, color: '#3973bf' }}>
                    {daysUntil(nextAppt.appointmentDate) > 0 ? `${daysUntil(nextAppt.appointmentDate)} Gün Sonra` : 'Bugün'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Yaklaşan Ödev — spans full column height (top aligns with Hikayeler) ── */}
        <div
          className="w-full xl:w-[440px] 2xl:w-[520px] xl:flex-shrink-0 bg-[#fafafa] rounded-[20px] p-4 flex flex-col max-h-[420px] xl:max-h-none"
          style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}
        >
          <p style={{ ...F, fontSize: 18, fontWeight: 600, color: '#3973bf', marginBottom: 10 }}>Teslim Tarihi Yaklaşan Ödevler</p>
          {loading ? (
            <div className="flex justify-center py-6"><div className="w-6 h-6 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
          ) : upcomingHomework.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-6">Yaklaşan ödev yok.</p>
          ) : (
            <div className="space-y-2.5 flex-1 min-h-0 overflow-y-auto pr-1">
              {upcomingHomework.map((hw) => (
                <div
                  key={hw.id}
                  onClick={() => navigate('/teacher/odevlerim')}
                  className="rounded-[16px] px-4 py-2.5 flex items-center cursor-pointer hover:shadow-sm transition-shadow"
                  style={{
                    // Top border uses the Hikayeler/Stories gradient; bottom + sides keep
                    // the original alternating color. The transparent top border shows
                    // the gradient background-image through, while the opaque side/bottom
                    // borders mask the gradient elsewhere.
                    borderStyle: 'solid',
                    borderWidth: 2,
                    borderTopColor: 'transparent',
                    borderRightColor: hw.color,
                    borderBottomColor: hw.color,
                    borderLeftColor: hw.color,
                    backgroundImage:
                      'linear-gradient(#fafafa, #fafafa), linear-gradient(135deg, #e5599b 0%, #7a07de 50%, #3973bf 100%)',
                    backgroundOrigin: 'border-box',
                    backgroundClip: 'padding-box, border-box',
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <p style={{ ...F, fontSize: 14, fontWeight: 600, color: '#2b2b2b' }} className="truncate">{hw.subject}</p>
                    <p style={{ ...F, fontSize: 13, fontWeight: 500, color: '#888' }}>{hw.detail}</p>
                  </div>
                  <div className="flex-shrink-0 text-center w-[64px]">
                    <p style={{ ...F, fontSize: 14, fontWeight: 700, color: hw.color }}>
                      {hw.days > 0 ? `${hw.days}G` : 'Bugün'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Sınıflarım */}
      <h2 className="cursor-pointer hover:text-[#3973bf] transition-colors mb-3"
        onClick={() => navigate('/teacher/siniflarim')}
        style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b' }}>
        Sınıflarım
      </h2>
      {loading ? (
        <div className="flex justify-center py-6"><div className="w-6 h-6 border-4 border-[#3973bf] border-t-transparent rounded-full animate-spin" /></div>
      ) : displayClasses.length === 0 ? (
        <p className="text-gray-400 text-sm mb-4">Sınıf bulunamadı.</p>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-3 mb-5">
          {displayClasses.map((c, i) => (
            <div key={c.id} onClick={() => navigate('/teacher/siniflarim', { state: { classId: c.id } })}
              className="bg-[#fafafa] rounded-[16px] w-[200px] xl:w-[220px] flex-shrink-0 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
              style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
              <div className="h-[90px] overflow-hidden">
                <img src={c.cover} alt="" className="w-full h-full object-cover object-center" />
              </div>
              <div className="p-3">
                <p style={{ ...F, fontSize: 16, fontWeight: 500, color: '#2b2b2b' }}>{c.name}</p>
                <p style={{ ...F, fontSize: 14, fontWeight: 500, color: '#2b2b2b', marginTop: 2 }}>{c.code}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <div className="flex items-center gap-1">
                    <svg width="16" height="13" viewBox="0 0 24 20" fill="#888"><path d="M12 12a5 5 0 100-10 5 5 0 000 10zm0 2c-5.33 0-8 2.67-8 4v2h16v-2c0-1.33-2.67-4-8-4z" /></svg>
                    <span style={{ ...F, fontSize: 13, fontWeight: 500, color: '#888' }}>{c.students}</span>
                  </div>
                  <button onClick={e => e.stopPropagation()} className="text-[#888] text-lg">•••</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Kitaplarım — from real library API */}
      <h2 className="cursor-pointer hover:text-[#3973bf] transition-colors mb-3"
        onClick={() => navigate('/teacher/kutuphanem')}
        style={{ ...F, fontSize: 22, fontWeight: 600, color: '#2b2b2b' }}>
        Kitaplarım
      </h2>
      <div className="flex gap-4 overflow-x-auto pb-3 mb-5">
        {books.length === 0 && !loading ? (
          <p className="text-gray-400 text-sm py-4">Kitap bulunamadı.</p>
        ) : books.map((b: any, i: number) => (
          <div key={b.id || i} onClick={() => navigate('/teacher/kutuphanem')}
            className="bg-[#fafafa] rounded-[12px] w-[200px] xl:w-[240px] flex-shrink-0 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
            style={{ boxShadow: '0px 1px 4px rgba(0,0,0,0.12)' }}>
            <div className="h-[120px] overflow-hidden">
              <img src={b.cover_url || b.cover_image_url || `/a-book${(i % 5) + 1}.png`} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="p-3">
              <p className="line-clamp-2 mb-1" style={{ ...F, fontSize: 14, fontWeight: 500, color: '#2b2b2b' }}>{b.title}</p>
              {b.subject?.name && <p style={{ ...F, fontSize: 12, color: '#3973bf' }}>{b.subject.name}</p>}
              {b.grade_levels?.grade_number && <p style={{ ...F, fontSize: 12, color: '#888' }}>{b.grade_levels.grade_number}. Sınıf</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TeacherDashboard;
